import TagManager from 'react-gtm-module';

export interface GTMEvent {
    event: string;
    [key: string]: any;
}

class GTMHelper {
    private isInitialized = false;

    /**
     * Initialize Google Tag Manager
     */
    initialize(containerId?: string): void {
        const gtmId = containerId || import.meta.env.VITE_GTM_CONTAINER_ID;

        // Only initialize if GTM container ID is provided
        if (!gtmId) {
            console.log('GTM container ID not found in environment variables. GTM tracking disabled.');
            return;
        }

        if (this.isInitialized) {
            console.warn('GTM is already initialized');
            return;
        }

        const tagManagerArgs = {
            gtmId,
        };

        TagManager.initialize(tagManagerArgs);
        this.isInitialized = true;
        console.log(`GTM initialized with container ID: ${gtmId}`);
    }

    /**
     * Send a custom event to GTM
     */
    sendEvent(event: GTMEvent): void {
        if (!this.isInitialized) {
            console.debug('GTM is not initialized. Event not sent:', event);
            return;
        }

        TagManager.dataLayer({
            dataLayer: event,
        });
    }

    /**
     * Track page views
     */
    trackPageView(pagePath: string, pageTitle?: string): void {
        if (!this.isInitialized) {
            console.debug('GTM is not initialized. Page view not tracked:', pagePath);
            return;
        }

        this.sendEvent({
            event: 'page_view',
            page_path: pagePath,
            page_title: pageTitle || document.title,
        });
    }

    /**
     * Track user interactions
     */
    trackEvent(action: string, category: string, label?: string, value?: number): void {
        if (!this.isInitialized) {
            console.debug('GTM is not initialized. Event not tracked:', { action, category, label, value });
            return;
        }

        this.sendEvent({
            event: 'custom_event',
            action,
            category,
            label,
            value,
        });
    }

    /**
     * Track form submissions
     */
    trackFormSubmission(formName: string, formId?: string): void {
        this.sendEvent({
            event: 'form_submit',
            form_name: formName,
            form_id: formId,
        });
    }

    /**
     * Track downloads
     */
    trackDownload(fileName: string, fileType: string, downloadUrl: string): void {
        this.sendEvent({
            event: 'file_download',
            file_name: fileName,
            file_type: fileType,
            download_url: downloadUrl,
        });
    }

    /**
     * Track user engagement
     */
    trackEngagement(engagementType: string, details: Record<string, any> = {}): void {
        this.sendEvent({
            event: 'user_engagement',
            engagement_type: engagementType,
            ...details,
        });
    }

    /**
     * Check if GTM is initialized
     */
    isReady(): boolean {
        return this.isInitialized;
    }
}

// Export a singleton instance
export const gtmService = new GTMHelper();
export default gtmService;
